import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { border, colors, distances } from '../../../styles/constants';
import { DateTime } from '../../../components/Formatters';
import Card from '../../../components/Card';

import { H3, P } from '../../../components/Typography';
import { OrderComment, OrderWithEvents } from '../../../types/orders';
import AddCommentForm from './AddCommentForm';
import { AccountAdminUser } from '../../../types/management-auth';
import Icon from '../../../components/Icons';
import Hr from '../../../components/Hr';
export interface CommentsProps extends WithTranslation {
    order: OrderWithEvents;
    accountId: string;
    createComment: (accountId: string, orderId: string, createCommentRequest: { body: string }) => void;
    deleteComment: (accountId: string, orderId: string, commentId: string) => void;
    createCommentIsLoading: boolean;
    users: AccountAdminUser[];
}

const usersName = (users: AccountAdminUser[], createdBy?: string) => {
    if (!createdBy) {
        return createdBy;
    }
    const alteredCreatedBy = createdBy.replace('mailto:', '');
    const find = users.find((user) => {
        return user.id === alteredCreatedBy || user.email === alteredCreatedBy;
    });
    return find?.name ?? alteredCreatedBy;
};

const Comments = (
    {
        t,
        order,
        accountId,
        createComment,
        deleteComment,
        createCommentIsLoading,
        users,
    }: CommentsProps
) => {
    const comments: OrderComment[] = order.comments || [];
    return (
        <Card marginBottom={distances.normal}>
            <H3>{t('orders.order.comments.title')}</H3>
            <Hr />
            <div>
                {comments.map((comment: OrderComment, i: number) => {
                    const visibility = comment.visibility || 'public';
                    return (
                        <CommentWrapper key={i}>
                            <SpeechBubble>
                                <P>{comment.body}</P>
                            </SpeechBubble>
                            <MetaWrapper>
                                <Author>{usersName(users, comment.created_by)}</Author>
                                <DateTime isoString={comment.created_at || ''} />
                                <Visibility visibility={visibility}>
                                    {visibility === 'public' && <Eye fill={colors.primary} icon="eye" />}
                                    {visibility !== 'public' && (
                                        <Eye fill={colors.textSecondary} icon="eyeStrikethrough" />
                                    )}
                                    {t('orders.order.comments.visibility', { context: visibility })}
                                </Visibility>
                                <DeleteButton
                                    onClick={() => {
                                        deleteComment(accountId, order.order_id as string, comment.id as string);
                                    }}
                                >
                                    <Delete icon="delete" fill={'currentColor'} />
                                </DeleteButton>
                            </MetaWrapper>
                        </CommentWrapper>
                    );
                })}
                <AddCommentForm
                    accountId={accountId}
                    orderId={order.order_id as string}
                    createComment={createComment}
                    createCommentIsLoading={createCommentIsLoading}
                />
            </div>
        </Card>
    );
};

export default withTranslation()(Comments);

const Eye = styled(Icon)`
    > svg {
        width: 20px;
    }
    margin-right: 5px;
    color: ${colors.primary};
`;

const Delete = styled(Icon)`
    > svg {
        width: 20px;
    }
    margin-left: 5px;
    margin-top: -10px;
`;



const CommentWrapper = styled.div`
    padding-top: 0;
    padding-bottom: 22px;
    width: 100%;
    font-size: 11px;
    position: relative;

    &:first-child:last-child:before {
        display: none;
    }
`;

interface VisibilityProps {
    visibility: 'private' | 'public';
}

const Visibility = styled.div<VisibilityProps>`
    margin-left: auto;
    color: ${(props) => (props.visibility === 'public' ? colors.primary : colors.textSecondary)};
`;

const DeleteButton = styled.button`
    display: block;
    margin-left: auto;
    border: none;
    background: none;
    margin: 0;
    padding: 0;
    color: ${colors.textSecondary};
    cursor: pointer;
    &:hover {
        color: ${colors.primary};
    }
    &:focus {
        box-shadow: none;
        outline: none;
    }
`;

const SpeechBubble = styled.div`
    margin: 10px auto;
    border: 2px solid rgba(242, 242, 242, 1);
    padding: 10px 20px;
    position: relative;

    &:before {
        content: '';
        width: 0;
        height: 0;
        position: absolute;
        border-left: 6px solid rgba(242, 242, 242, 1);
        border-right: 6px solid transparent;
        border-top: 6px solid rgba(242, 242, 242, 1);
        border-bottom: 6px solid transparent;
        left: 15px;
        bottom: -12px;
    }

    &:after {
        content: '';
        width: 0;
        height: 0;
        position: absolute;
        border-left: 6px solid #fff;
        border-right: 6px solid transparent;
        border-top: 6px solid #fff;
        border-bottom: 6px solid transparent;
        left: 17px;
        bottom: -7px;
    }
`;

const Author = styled.div`
    padding-right: ${distances.small};
    font-weight: bold;
`;

const MetaWrapper = styled.div`
    display: flex;
`;
